// Media upload helpers — PUT to the existing Cloudflare R2 worker
// (media.ebikesflorence.com), the SAME endpoint + auth contract the Flutter app
// uses (lib/services/r2_storage_service.dart). Contract verified against the
// deployed worker source:
//
//   Single PUT : PUT  /upload/<key>                                  -> { success, key, size, etag, url }
//   Multipart  : POST /multipart/create/<key>                        -> { uploadId, key }
//                PUT  /multipart/part/<key>?uploadId=&partNumber=N    -> { partNumber, etag }
//                POST /multipart/complete/<key>?uploadId=  body {parts} -> { success, key, etag, url }
//                POST /multipart/abort/<key>?uploadId=                -> { success }
//   Public read: GET  /<key>   (no auth, edge-cached)
//   Delete     : DELETE <workers.dev>/<key>  (custom domain WAF blocks DELETE)
//
// Auth: Authorization: Bearer <supabase access_token> (ES256 JWT, verified by the
// worker via JWKS). The worker returns Access-Control-Allow-Origin: * on every
// route, so browser-origin uploads from the Pages app / localhost are allowed.

import { supabase } from './supabase';

const CDN_BASE = 'https://media.ebikesflorence.com';
// DELETE is blocked by Cloudflare WAF on the custom domain; the worker's
// workers.dev hostname bypasses it (same worker, same '*' CORS).
const WORKER_DEV_BASE = 'https://locationtrigger-media-worker.ebikesflorence.workers.dev';

/** Above this size a single PUT is replaced by a multipart upload (matches Flutter). */
const MAX_SINGLE_PUT_BYTES = 95 * 1024 * 1024;
/** Multipart chunk size (10 MB, matches Flutter). */
const MULTIPART_CHUNK_BYTES = 10 * 1024 * 1024;

const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // ceiling for the FINAL (post-downscale) upload
// Pre-downscale sanity cap on the source file. A modern phone photo (e.g. a 48 MP
// JPEG at 12–25 MB) exceeds MAX_IMAGE_BYTES but downscales to well under it, so we
// only reject genuinely huge source files here and re-check after downscaling.
const MAX_IMAGE_SOURCE_BYTES = 64 * 1024 * 1024;
const MAX_VIDEO_BYTES = 1024 * 1024 * 1024; // gallery videos (1 GB)
const DOWNSCALE_MAX_DIM = 2048; // px — cap longest edge before upload
const POSTER_MAX_DIM = 720; // px — cap longest edge of a generated video poster

const ALLOWED_IMAGE_MIME = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
const ALLOWED_VIDEO_MIME = new Set(['video/mp4', 'video/quicktime', 'video/webm']);

export type ImageKind = 'logo' | 'banner' | 'cover';
export type GalleryItemType = 'image' | 'video';

export interface UploadError extends Error {
  status?: number;
  aborted?: boolean;
}

function uploadError(message: string, opts: { status?: number; aborted?: boolean } = {}): UploadError {
  const e = new Error(message) as UploadError;
  if (opts.status !== undefined) e.status = opts.status;
  if (opts.aborted) e.aborted = true;
  return e;
}

function extFromMime(mime: string): string {
  switch (mime) {
    case 'image/jpeg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/webp':
      return 'webp';
    case 'image/gif':
      return 'gif';
    case 'video/mp4':
      return 'mp4';
    case 'video/quicktime':
      return 'mov';
    case 'video/webm':
      return 'webm';
    default:
      return 'bin';
  }
}

export function humanSize(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** Current access token — fetched per upload so it never goes stale mid-session. */
async function getAccessToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw uploadError('You are signed out — sign in again to upload.', { status: 401 });
  return token;
}

/** Public CDN URL for a stored key (optionally cache-busted for fixed keys). */
function cdnUrl(key: string, cacheBust = false): string {
  const base = `${CDN_BASE}/${key}`;
  return cacheBust ? `${base}?v=${Date.now()}` : base;
}

// ----------------------------------------------------------------------------
// Client-side downscale — cap the longest edge, re-encode. Returns the original
// file untouched if it's already small, not an image, or can't be decoded.
// ----------------------------------------------------------------------------
async function downscaleImage(file: File): Promise<Blob> {
  if (!ALLOWED_IMAGE_MIME.has(file.type)) return file;
  if (file.type === 'image/gif') return file; // never re-encode animated gifs
  if (typeof createImageBitmap !== 'function') return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }
  const { width, height } = bitmap;
  const longest = Math.max(width, height);
  if (longest <= DOWNSCALE_MAX_DIM) {
    bitmap.close();
    return file;
  }
  const scale = DOWNSCALE_MAX_DIM / longest;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const outType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const quality = outType === 'image/jpeg' ? 0.85 : undefined;
  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), outType, quality),
  );
  if (!blob || blob.size >= file.size) return file;
  return blob;
}

// ----------------------------------------------------------------------------
// Video poster — decode the selected video in an off-screen <video>, grab one
// early frame, and return it as a JPEG. The media worker doesn't support HTTP
// Range requests, so neither the app nor a server can cheaply pull a frame from
// the stored video later — so we capture it HERE at upload time, where the full
// file is already in hand. Best-effort: returns null on any decode/seek failure
// (undecodable codec, no document, missing canvas) so the caller can skip the
// poster without failing the video upload.
// ----------------------------------------------------------------------------
async function generateVideoPoster(file: File): Promise<Blob | null> {
  if (typeof document === 'undefined') return null;

  const objectUrl = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.preload = 'auto';
  video.setAttribute('playsinline', ''); // Safari: decode without entering fullscreen
  video.src = objectUrl;

  // Resolve when `event` fires; resolve anyway after `timeoutMs` so a browser
  // that never emits the event (e.g. a no-op seek) can't hang the upload. Only
  // an explicit media 'error' rejects.
  const settle = (event: string, timeoutMs: number): Promise<void> =>
    new Promise((resolve, reject) => {
      let done = false;
      const finish = (fn: () => void): void => {
        if (done) return;
        done = true;
        video.removeEventListener(event, onEvent);
        video.removeEventListener('error', onError);
        clearTimeout(timer);
        fn();
      };
      const onEvent = (): void => finish(resolve);
      const onError = (): void => finish(() => reject(new Error(`video ${event} failed`)));
      const timer = setTimeout(() => finish(resolve), timeoutMs);
      video.addEventListener(event, onEvent, { once: true });
      video.addEventListener('error', onError, { once: true });
    });

  try {
    await settle('loadeddata', 8000); // metadata + first frame decoded
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const target = Math.min(1, duration * 0.1); // ~min(1s, 10% of duration)
    if (target > 0.05) {
      video.currentTime = target;
      await settle('seeked', 8000);
    }

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;

    const longest = Math.max(vw, vh);
    const scale = longest > POSTER_MAX_DIM ? POSTER_MAX_DIM / longest : 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(vw * scale);
    canvas.height = Math.round(vh * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    return await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.8),
    );
  } finally {
    video.removeAttribute('src');
    video.load(); // abort any in-flight decode + release the buffer
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Best-effort: generate a poster frame for a just-uploaded gallery video and
 * store it at the SAME object path with a `.jpg` extension — the convention the
 * apps derive (`gallery/<uuid>.mp4` -> `gallery/<uuid>.jpg`). A poster is a
 * plain JPEG, so it goes through the normal photo single-PUT path (CDN_BASE) —
 * NOT the workers.dev video bypass. The poster is a sibling object, NOT a
 * gallery item: it is never added to media_urls/media_types. Any failure here
 * is swallowed (logged) — the video upload has already succeeded and the apps
 * fall back to a black-box placeholder when no poster exists.
 */
async function uploadVideoPoster(file: File, posterKey: string, token: string): Promise<void> {
  try {
    const poster = await generateVideoPoster(file);
    if (!poster) return;
    await putSingle(posterKey, poster, 'image/jpeg', token);
  } catch (e) {
    console.warn('[media] video poster generation/upload failed (non-fatal):', e);
  }
}

// ----------------------------------------------------------------------------
// XHR PUT — gives real upload progress (fetch can't report request-body progress).
// ----------------------------------------------------------------------------
function xhrPut(
  url: string,
  body: Blob,
  token: string,
  contentType: string,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<{ status: number; text: string; etagHeader: string | null }> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(uploadError('Upload cancelled.', { aborted: true }));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', contentType);
    const onAbort = (): void => xhr.abort();
    if (signal) signal.addEventListener('abort', onAbort, { once: true });
    const cleanup = (): void => {
      if (signal) signal.removeEventListener('abort', onAbort);
    };
    if (onProgress) {
      xhr.upload.onprogress = (e): void => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = (): void => {
      cleanup();
      resolve({ status: xhr.status, text: xhr.responseText, etagHeader: xhr.getResponseHeader('etag') });
    };
    xhr.onerror = (): void => {
      cleanup();
      reject(
        uploadError(
          'Network error reaching the media server. Check your connection and try again.',
        ),
      );
    };
    xhr.onabort = (): void => {
      cleanup();
      reject(uploadError('Upload cancelled.', { aborted: true }));
    };
    xhr.send(body);
  });
}

// ----------------------------------------------------------------------------
// Single PUT
// ----------------------------------------------------------------------------
async function putSingle(
  key: string,
  body: Blob,
  contentType: string,
  token: string,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
  base: string = CDN_BASE,
): Promise<void> {
  const { status, text } = await xhrPut(`${base}/upload/${key}`, body, token, contentType, onProgress, signal);
  if (status < 200 || status >= 300) {
    throw uploadError(`Upload failed (${status}): ${text || 'no response body'}`, { status });
  }
}

// ----------------------------------------------------------------------------
// Multipart (large videos) — custom worker paths, verified against the worker.
// ----------------------------------------------------------------------------
async function abortMultipart(
  key: string,
  uploadId: string,
  token: string,
  base: string = CDN_BASE,
): Promise<void> {
  await fetch(`${base}/multipart/abort/${key}?uploadId=${encodeURIComponent(uploadId)}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  }).catch(() => undefined);
}

async function putMultipart(
  key: string,
  file: Blob,
  contentType: string,
  token: string,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
  base: string = CDN_BASE,
): Promise<void> {
  const createResp = await fetch(`${base}/multipart/create/${key}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': contentType },
  });
  if (!createResp.ok) throw uploadError(`Multipart create failed (${createResp.status})`, { status: createResp.status });
  const created = (await createResp.json()) as { uploadId?: string };
  const uploadId = created.uploadId;
  if (!uploadId) throw uploadError('Multipart create returned no uploadId.');

  const totalParts = Math.ceil(file.size / MULTIPART_CHUNK_BYTES);
  const parts: Array<{ partNumber: number; etag: string }> = [];
  try {
    for (let i = 0; i < totalParts; i++) {
      if (signal?.aborted) throw uploadError('Upload cancelled.', { aborted: true });
      const start = i * MULTIPART_CHUNK_BYTES;
      const chunk = file.slice(start, Math.min(start + MULTIPART_CHUNK_BYTES, file.size));
      const partNumber = i + 1;
      const { status, text, etagHeader } = await xhrPut(
        `${base}/multipart/part/${key}?uploadId=${encodeURIComponent(uploadId)}&partNumber=${partNumber}`,
        chunk,
        token,
        'application/octet-stream',
        (frac) => onProgress?.((i + frac) / totalParts),
        signal,
      );
      if (status !== 200) throw uploadError(`Part ${partNumber} failed (${status})`, { status });
      let etag: string | null = etagHeader;
      if (text) {
        try {
          etag = (JSON.parse(text) as { etag?: string }).etag ?? etag;
        } catch {
          /* keep header etag */
        }
      }
      if (!etag) throw uploadError(`Part ${partNumber} returned no ETag.`);
      parts.push({ partNumber, etag });
    }
  } catch (err) {
    await abortMultipart(key, uploadId, token, base);
    throw err;
  }

  const completeResp = await fetch(`${base}/multipart/complete/${key}?uploadId=${encodeURIComponent(uploadId)}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ parts }),
  });
  if (!completeResp.ok) {
    await abortMultipart(key, uploadId, token, base);
    throw uploadError(`Multipart complete failed (${completeResp.status})`, { status: completeResp.status });
  }
  onProgress?.(1);
}

// ----------------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------------

/** Upload a logo / banner / cover image. Returns the cache-busted CDN URL. */
export async function uploadImage(
  kind: ImageKind,
  file: File,
  userId: string,
  onProgress?: (fraction: number) => void,
): Promise<string> {
  if (!ALLOWED_IMAGE_MIME.has(file.type)) {
    throw uploadError('Please choose a JPEG, PNG, WebP, or GIF image.');
  }
  if (file.size > MAX_IMAGE_SOURCE_BYTES) {
    throw uploadError(`Image is too large (${humanSize(file.size)}). Max ${humanSize(MAX_IMAGE_SOURCE_BYTES)}.`);
  }
  const token = await getAccessToken();
  // Downscale FIRST (caps the longest edge at 2048px), then enforce the upload
  // ceiling — a large straight-from-phone photo shrinks under the limit instead
  // of being rejected outright.
  const body = await downscaleImage(file);
  if (body.size > MAX_IMAGE_BYTES) {
    throw uploadError(`Image is still too large after downscaling (${humanSize(body.size)}). Max ${humanSize(MAX_IMAGE_BYTES)} — try compressing it (tinypng.com).`);
  }
  const key = `providers/${userId}/${kind}.${extFromMime(file.type)}`;
  await putSingle(key, body, body.type || file.type, token, onProgress);
  return cdnUrl(key, true); // fixed key → cache-bust so re-uploads show immediately
}

/** Upload one gallery item (photo single-PUT; video multipart if large). */
export async function uploadGalleryItem(
  file: File,
  userId: string,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<{ url: string; type: GalleryItemType }> {
  const isImage = ALLOWED_IMAGE_MIME.has(file.type);
  const isVideo = ALLOWED_VIDEO_MIME.has(file.type);
  if (!isImage && !isVideo) {
    throw uploadError('Gallery accepts JPEG/PNG/WebP/GIF images or MP4/MOV/WebM videos.');
  }
  const cap = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_SOURCE_BYTES;
  if (file.size > cap) {
    throw uploadError(`File is too large (${humanSize(file.size)}). Max ${humanSize(cap)}.`);
  }

  const token = await getAccessToken();
  const uuid = crypto.randomUUID();
  const key = `providers/${userId}/gallery/${uuid}.${extFromMime(file.type)}`;

  if (isVideo) {
    // Route video uploads through the workers.dev host (NOT the orange-clouded
    // custom domain) — the same WAF bypass deleteMediaByUrl uses. Large/raw video
    // PUTs and the multipart POST/PUT sequence are blocked by the custom-domain
    // Cloudflare WAF (which also blocks DELETE, see above), so the upload throws,
    // uploadOne's catch swallows it, and the video URL never reaches media_urls —
    // leaving photo-only galleries. Small downscaled photos (<=10MB) pass the WAF,
    // so they stay on CDN_BASE. Reads are still served from the CDN via cdnUrl().
    if (file.size > MAX_SINGLE_PUT_BYTES) {
      await putMultipart(key, file, file.type, token, onProgress, signal, WORKER_DEV_BASE);
    } else {
      await putSingle(key, file, file.type, token, onProgress, signal, WORKER_DEV_BASE);
    }
    // The video is safely stored. Now capture + store a poster frame at the
    // sibling `<uuid>.jpg` key (best-effort) so the apps show a thumbnail
    // instead of a black box. Keyed off `uuid` so the poster path matches
    // regardless of the video's extension; never blocks/fails the upload above.
    const posterKey = `providers/${userId}/gallery/${uuid}.jpg`;
    await uploadVideoPoster(file, posterKey, token);
    return { url: cdnUrl(key), type: 'video' };
  }

  const body = await downscaleImage(file);
  if (body.size > MAX_IMAGE_BYTES) {
    throw uploadError(`Image is still too large after downscaling (${humanSize(body.size)}). Max ${humanSize(MAX_IMAGE_BYTES)} — try compressing it (tinypng.com).`);
  }
  await putSingle(key, body, body.type || file.type, token, onProgress, signal);
  return { url: cdnUrl(key), type: 'image' };
}

/**
 * Upload one menu photograph for distillation (T57 WP3).
 *
 * Separate from `uploadGalleryItem` on purpose: a menu photo is evidence, not
 * gallery content. It gets its own `menus/` prefix so it never leaks into the
 * public gallery, and it is image-only — there is nothing to read in a video.
 *
 * The returned URL is on CDN_BASE, which is the single host
 * `distill-provider-media` will accept (its SAOIRSE_MEDIA_PHOTO_HOSTS
 * allowlist). Uploading anywhere else means the Edge Function refuses the link.
 *
 * Downscaling to 2048px on the longest edge is inherited from `downscaleImage`
 * and is deliberate: it keeps a phone photo under the upload ceiling while
 * leaving printed menu text comfortably legible to the vision model.
 */
export async function uploadMenuPhoto(
  file: File,
  userId: string,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<string> {
  if (!ALLOWED_IMAGE_MIME.has(file.type)) {
    throw uploadError('Please photograph the menu as a JPEG, PNG or WebP image.');
  }
  if (file.size > MAX_IMAGE_SOURCE_BYTES) {
    throw uploadError(`Photo is too large (${humanSize(file.size)}). Max ${humanSize(MAX_IMAGE_SOURCE_BYTES)}.`);
  }
  const token = await getAccessToken();
  const key = `providers/${userId}/menus/${crypto.randomUUID()}.${extFromMime(file.type)}`;
  const body = await downscaleImage(file);
  if (body.size > MAX_IMAGE_BYTES) {
    throw uploadError(`Photo is still too large after downscaling (${humanSize(body.size)}). Max ${humanSize(MAX_IMAGE_BYTES)}.`);
  }
  await putSingle(key, body, body.type || file.type, token, onProgress, signal);
  return cdnUrl(key);
}

/** Best-effort delete of a previously uploaded object (gallery remove). */
export async function deleteMediaByUrl(url: string): Promise<void> {
  let key: string;
  try {
    key = decodeURIComponent(new URL(url).pathname.replace(/^\//, ''));
  } catch {
    return;
  }
  if (!key) return;
  let token: string;
  try {
    token = await getAccessToken();
  } catch {
    return; // signed out — skip; the row reference is already gone
  }
  // DELETE at the root path on the workers.dev host (custom domain WAF blocks it).
  await fetch(`${WORKER_DEV_BASE}/${key}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  }).catch(() => undefined);
}

export const MEDIA_LIMITS = {
  maxImageBytes: MAX_IMAGE_BYTES,
  maxVideoBytes: MAX_VIDEO_BYTES,
  maxGalleryItems: 5,
} as const;
